import { useEffect, useState } from "react";
import { environment } from "../environments/environment";
import api from "../api/api";
import { Skeleton } from "@mui/material";

interface ProductImage {
  media_url: string;
  uuid: string;
}

interface ProductDetails {
  product: {
    uuid: string;
    product_title: string;
    friends_price: number;
    minimum_quantity: number;
    size_kg: string;
  };
  product_images: ProductImage[];
}

export const ProductSummary = ({ uuid, itemCount }: { uuid: string, itemCount: string }) => {
  const [productDetails, setProductDetails] = useState<ProductDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const currencyView = "$"
  
  const totalPrice = productDetails?.product.friends_price || 0;

    useEffect(() => {
      const fetchProduct = async () => {
        try {
          setIsLoading(true);
          const response = await api.get(
            `${environment.baseUrl}/product/fetch/single/${uuid}`
          );
          setProductDetails(response.data.data);
        } catch (error) {
          console.error("Error fetching product details:", error);
        } finally {
          setIsLoading(false);
        }
      };
  
      if (uuid) {
        fetchProduct();
      }
    }, [uuid]);
  
    if (isLoading) {
      return (
        <div className="md:col-span-4 bg-gray-50 p-6 rounded-xl">
          <h3 className="text-lg font-bold mb-4">Order Summary</h3>
          <div className="flex flex-col space-y-4">
            <Skeleton variant="rectangular" height={200} />
            <Skeleton variant="text" height={30} />
            <Skeleton variant="text" height={20} />
            <Skeleton variant="text" height={20} />
            <Skeleton variant="rectangular" height={1} />
            <Skeleton variant="text" height={30} />
          </div>
        </div>
      );
    }
  
    if (!productDetails) {
      return (
        <div className="md:col-span-4 bg-gray-50 p-6 rounded-xl">
          <h3 className="text-lg font-bold mb-4">Order Summary</h3>
          <p className="text-red-500">Product information not available</p>
        </div>
      );
    }

  

  return (
    <div className="md:col-span-4 bg-gray-50 p-6 rounded-xl h-fit sticky top-6">
      <h3 className="text-lg font-bold mb-4">Order Summary</h3>

      {/* Product Image */}
      <div className="mb-4 overflow-hidden rounded-lg">
        {productDetails.product_images && productDetails.product_images.length > 0 && (
          <img 
            src={productDetails.product_images[0].media_url} 
            alt={productDetails.product.product_title}
            className="w-full h-48 object-cover"
          />
        )}
      </div>

      {/* Product Details */}
      <div className="mb-6">
        <h4 className="font-bold text-lg mb-1">{productDetails.product.product_title}</h4>
        
        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mt-3">
          <div>
            <span className="font-medium">Size:</span>
          </div>
          <div className="text-right">
            {productDetails.product.size_kg} kg
          </div>
          
          <div>
            <span className="font-medium">Min. Quantity:</span> 
          </div>
          <div className="text-right">
            {productDetails.product.minimum_quantity} items
          </div>
          
          <div>
            <span className="font-medium">Your Quantity:</span>
          </div>
          <div className="text-right">
            {itemCount} items
          </div>
        </div>
      </div>
      
      {/* Divider */}
      <div className="border-b border-gray-200 my-4"></div>
      
      {/* Price Summary */}
      <div className="flex flex-col space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Unit Price:</span>
          <span className="font-medium">
            {currencyView} {productDetails?.product.friends_price.toLocaleString()}
          </span>
        </div>
        
        <div className="flex justify-between items-center">
          <span className="text-gray-600">Quantity:</span>
          <span>{itemCount}</span>
        </div>
        
        <div className="border-b border-gray-200 my-2"></div>
        
        <div className="flex justify-between items-center text-lg font-bold">
          <span>Total:</span>
          <span>
            {currencyView} {totalPrice.toLocaleString()}
          </span>
        </div>
      </div>
      </div>
  )

}